import React from 'react';

export interface LiveChoiceBarItem {
  label: string;
  count: number;
  accent?: string;
  icon?: string;
  isCorrect?: boolean;
  isSelected?: boolean;
}

interface LiveChoiceBarsProps {
  items: LiveChoiceBarItem[];
  total: number;
  size?: 'md' | 'lg';
  showCounts?: boolean;
  revealCorrect?: boolean;
}

export function LiveChoiceBars({
  items,
  total,
  size = 'md',
  showCounts = true,
  revealCorrect = false,
}: LiveChoiceBarsProps) {
  const barH = size === 'lg' ? 'py-5' : 'py-3';
  const textSize = size === 'lg' ? 'text-xl sm:text-2xl' : 'text-base';

  return (
    <div className="space-y-3">
      {items.map((item, i) => {
        const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
        const accent = item.accent ?? `hsl(${(i * 67) % 360} 70% 55%)`;
        const showCorrect = revealCorrect && item.isCorrect;

        return (
          <div
            key={`${item.label}-${i}`}
            className={`relative overflow-hidden rounded-xl border-2 transition-all duration-500 ${barH}`}
            style={{
              borderColor: showCorrect
                ? '#22c55e'
                : item.isSelected
                  ? accent
                  : 'var(--plugin-border)',
              background: 'var(--plugin-btn-bg)',
              boxShadow: showCorrect ? '0 0 24px rgba(34,197,94,0.35)' : undefined,
            }}
          >
            <div
              className="absolute inset-y-0 left-0 transition-all duration-700 ease-out"
              style={{
                width: `${pct}%`,
                background: accent,
                opacity: 0.4,
              }}
            />
            <div className={`relative flex items-center justify-between gap-3 px-4 ${textSize}`}>
              <span className="flex min-w-0 items-center gap-2 font-semibold">
                {item.icon && <span className="shrink-0 text-2xl">{item.icon}</span>}
                <span className="truncate">{item.label}</span>
                {showCorrect && (
                  <span className="shrink-0 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                    Correcta
                  </span>
                )}
              </span>
              {showCounts && (
                <span className="shrink-0 font-display font-bold" style={{ color: 'var(--sala-accent-neon)' }}>
                  {pct}%
                  <span className="ml-1 text-xs font-normal opacity-70">({item.count})</span>
                </span>
              )}
            </div>
          </div>
        );
      })}
      <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--plugin-muted)' }}>
        {total} respuesta{total !== 1 ? 's' : ''}
      </p>
    </div>
  );
}
